import { useApolloClient, useSubscription } from '@apollo/client';
import {
  CacheKeys,
  ProjectAppTypes,
  useAppUser,
  useEnvProvider,
  USER_PROJECT_ID,
  useWorkPackageCache,
} from '@bechtel/client-common';
import { SUBSCRIPTION_WORKPACKAGE_UPDATED } from '@bechtel/client-gql';
import { CrewMember, PersonaType, WorkPackagesUpdatedPayload } from '@bechtel/types';
import { includes } from 'lodash';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { clearCacheByKey, QueryKeys } from '~/services/cache';

export function useWorkPackageSubscription() {
  const { data: workPackageSubscriptionData } = useSubscription<{ workPackagesUpdated: WorkPackagesUpdatedPayload }>(
    SUBSCRIPTION_WORKPACKAGE_UPDATED as any
  );
  const { updateWorkPackagesCache } = useWorkPackageCache();
  const { envConfig } = useEnvProvider();
  const isPFAppType = envConfig.appName === (ProjectAppTypes as any).PF;
  const location = useLocation();
  const { user } = useAppUser();
  const client = useApolloClient();
  //TODO:currently useSessionStorage not given value when select user it gives after refresh so used direct session storage
  // const [dashboardSelectedUser] = useSessionStorage<CrewMember>(CacheKeys.DashboardSelectedUser);
  const dashboardSelectedUser: CrewMember = JSON.parse(
    sessionStorage.getItem((CacheKeys as any).DashboardSelectedUser) || '{}'
  );
  const corePersonaTypes = [PersonaType.SUPERINTENDENT, PersonaType.GENERAL_FOREMAN, PersonaType.FOREMAN];
  const currentUserSelectedProject = window.sessionStorage.getItem(USER_PROJECT_ID);

  useEffect(() => {
    if (
      !workPackageSubscriptionData ||
      !workPackageSubscriptionData.workPackagesUpdated ||
      !workPackageSubscriptionData.workPackagesUpdated.projectId ||
      !currentUserSelectedProject ||
      workPackageSubscriptionData.workPackagesUpdated.projectId !== currentUserSelectedProject ||
      !workPackageSubscriptionData.workPackagesUpdated.workPackageIds ||
      workPackageSubscriptionData.workPackagesUpdated.workPackageIds.length == 0
    )
      return;
    updateWorkPackagesCache(workPackageSubscriptionData.workPackagesUpdated.workPackageIds);
    //Update the dashboard stat
  }, [workPackageSubscriptionData]);

  useEffect(() => {
    const fetchDashboardStatForSuperGfForeman = async () => {
      await client.refetchQueries({
        include: [
          'workPackages',
          'workPackageStats',
          'weeklyWorkSummaryStats',
          'weeklyWorkSummaryMilestones',
          'weeklyWorkList',
        ],
      });
    };

    const fetchDashboardStatForFieldEngineer = async () => {
      await client.refetchQueries({
        include: ['workPackages', 'workPackageStats', 'pendingApprovalSummary', 'weeklyWorkList'],
      });
    };

    const fetchWorkPackageCommodities = async () => {
      clearCacheByKey(client?.cache, QueryKeys.COMMODITIES);
      client?.cache.gc();
      // Skip refetching work packages list to preserve scroll position
      // Individual mutations will update the cache directly
      // await client.refetchQueries({
      //   include: ['workPackages'],
      // });
    };

    if (
      !workPackageSubscriptionData ||
      !workPackageSubscriptionData.workPackagesUpdated ||
      !workPackageSubscriptionData.workPackagesUpdated.projectId ||
      !currentUserSelectedProject ||
      workPackageSubscriptionData.workPackagesUpdated.projectId !== currentUserSelectedProject ||
      !workPackageSubscriptionData.workPackagesUpdated.affectedOwnerIds ||
      workPackageSubscriptionData.workPackagesUpdated.affectedOwnerIds.length == 0
    )
      return;

    //If not these four persona's Sup/Gf/Foreman/FE Return
    if (
      !(
        includes([...corePersonaTypes, PersonaType.FIELD_ENGINEER], user?.crewMember?.persona) ||
        includes([...corePersonaTypes, PersonaType.FIELD_ENGINEER], dashboardSelectedUser?.persona)
      )
    )
      return;

    const affectedOwnerIds = workPackageSubscriptionData.workPackagesUpdated.affectedOwnerIds;
    const pathName = location.pathname;
    if (
      isPFAppType &&
      affectedOwnerIds &&
      (includes(affectedOwnerIds, user?.id) || includes(affectedOwnerIds, dashboardSelectedUser?.id))
    ) {
      //if the user is Foreman/Gf/Super or SuperAdmin dashboard selected user is Foreman/Gf/Super
      if (pathName && pathName === '/') {
        if (
          includes(corePersonaTypes, user?.crewMember?.persona) ||
          includes(corePersonaTypes, dashboardSelectedUser?.persona)
        )
          fetchDashboardStatForSuperGfForeman();

        //if the user is Field Engineer or SuperAdmin dashboard selected user is Field Engineer
        if (
          includes([PersonaType.FIELD_ENGINEER], user?.crewMember?.persona) ||
          includes([PersonaType.FIELD_ENGINEER], dashboardSelectedUser?.persona)
        )
          fetchDashboardStatForFieldEngineer();
      }
      if (pathName && pathName.includes('/work-packages')) fetchWorkPackageCommodities();
    }
  }, [location, workPackageSubscriptionData, dashboardSelectedUser]);

  return null;
}
